/**
 * LIFF Mock Implementation
 * Used for local browser testing without LINE app
 */

// LocalStorage key for mock mode persistence
const LIFF_MOCK_KEY = 'liff-mock-enabled';

/**
 * Check if LIFF mock mode is allowed
 * Must have REACT_APP_USE_LIFF_MOCK=true (build-time) to enable mock functionality
 * 
 * @returns true if mock mode is allowed by build configuration
 */
export const isLiffMockAllowed = (): boolean => {
  return process.env.REACT_APP_USE_LIFF_MOCK === 'true';
};

/**
 * Check if LIFF mock mode should be enabled
 * Requires:
 * 1. REACT_APP_USE_LIFF_MOCK=true (build-time gate)
 * 2. URL parameter liff-mock=true OR localStorage liff-mock-enabled=true
 * 
 * @returns true if mock mode should be enabled
 */
export const isLiffMockEnabled = (): boolean => {
  // Gate: モック機能が許可されていなければ常にfalse
  if (!isLiffMockAllowed()) {
    console.log('[LIFF Mock] Mock mode not allowed (REACT_APP_USE_LIFF_MOCK !== true)');
    return false;
  }
  
  // Check URL parameter first (highest priority)
  const urlParams = new URLSearchParams(window.location.search);
  const urlMockParam = urlParams.get('liff-mock');
  
  if (urlMockParam === 'true') {
    // Enable mock mode and persist to localStorage
    localStorage.setItem(LIFF_MOCK_KEY, 'true');
    console.log('[LIFF Mock] Enabled via URL parameter, saved to localStorage');
    return true;
  } else if (urlMockParam === 'false') {
    // Disable mock mode and clear localStorage
    localStorage.removeItem(LIFF_MOCK_KEY);
    console.log('[LIFF Mock] Disabled via URL parameter, cleared localStorage');
    return false;
  }
  
  // Check localStorage (second priority)
  const storedMock = localStorage.getItem(LIFF_MOCK_KEY);
  if (storedMock === 'true') {
    console.log('[LIFF Mock] Enabled via localStorage');
    return true;
  }
  
  // Default: モック機能は許可されているが、明示的に有効化されていない
  console.log('[LIFF Mock] Mock allowed but not enabled (use ?liff-mock=true to enable)');
  return false;
};

/**
 * Disable LIFF mock mode
 * Clears localStorage and returns false
 */
export const disableLiffMock = (): void => {
  localStorage.removeItem(LIFF_MOCK_KEY);
  console.log('[LIFF Mock] Mock mode disabled');
};

export const createLiffMock = () => {
  const mockUserId = 'U' + Math.random().toString(36).substr(2, 9);
  const mockAccessToken = 'mock_token_' + Date.now();

  return {
    init: async (config: any) => {
      console.log('[LIFF Mock] Initialized with config:', config);
      return Promise.resolve();
    },

    isLoggedIn: () => {
      console.log('[LIFF Mock] isLoggedIn() -> true (mocked)');
      return true;
    },

    isInClient: () => {
      console.log('[LIFF Mock] isInClient() -> false (mocked, running in browser)');
      return false;
    },

    getAccessToken: () => {
      console.log('[LIFF Mock] getAccessToken() -> mocked token');
      return mockAccessToken;
    },

    getProfile: async () => {
      const profile = {
        userId: mockUserId,
        displayName: 'Test User',
        pictureUrl: 'https://via.placeholder.com/150',
        statusMessage: 'Testing nity-line-front',
      };
      console.log('[LIFF Mock] getProfile() -> ', profile);
      return profile;
    },

    getDecodedIDToken: () => {
      const token = {
        email: 'test@example.com',
        name: 'Test User',
        aud: 'mock_liff_id',
      };
      console.log('[LIFF Mock] getDecodedIDToken() -> ', token);
      return token;
    },

    login: () => {
      console.log('[LIFF Mock] login() called (no-op in mock)');
    },

    logout: () => {
      console.log('[LIFF Mock] logout() called (no-op in mock)');
    },

    openWindow: (params: { url: string; external?: boolean }) => {
      console.log('[LIFF Mock] openWindow() -> ', params.url);
      window.open(params.url, params.external ? '_blank' : '_self');
    },

    closeWindow: () => {
      console.log('[LIFF Mock] closeWindow() called (no-op in mock)');
    },
  };
};

/**
 * Setup LIFF mock in window object
 * Call this before initializing app if REACT_APP_USE_LIFF_MOCK=true
 */
export const setupLiffMock = () => {
  if (process.env.REACT_APP_USE_LIFF_MOCK !== 'true') {
    console.log('[LIFF] Using real LIFF SDK');
    return;
  }

  console.log('[LIFF] Setting up mock LIFF...');
  
  // Create mock and attach to window
  const liffMock = createLiffMock();
  (window as any).liff = liffMock;
  
  console.log('[LIFF Mock] Successfully set up in window.liff');
};
